import React from 'react';
import {Image, type StyleProp, type ImageStyle} from 'react-native';

import {useIsDark} from '@/shared/ui/theme';

/**
 * The SAMVISION wordmark — the same asset the desktop control app brands itself with
 * (samvision_controle_desktop/branding/logo.png), so a customer who sees both products
 * sees one company.
 *
 * Two files, one shape: `logo.png` is the original near-black wordmark, `logo-white.png`
 * is the SAME artwork with every pixel repainted white (alpha untouched), generated from
 * it rather than drawn again — the two cannot drift apart. The dark palette gets white
 * because the original ink (#1c1c1a) on a #0b1116 background is invisible; this is not a
 * "dark mode logo", it is the logo, legible.
 *
 * Sized by WIDTH only. The height follows the artwork's own 7.95:1 ratio, so no call
 * site can squash the letterforms by picking a wrong pair of numbers.
 */
const ASPECT = 1200 / 151;

// Static requires: Metro resolves these at bundle time, so both variants ship on
// native and neither is fetched over the wire.
const DARK_INK = require('../../../assets/branding/logo.png');
const WHITE_INK = require('../../../assets/branding/logo-white.png');

export function Logo({
  width,
  /** Force a variant; by default it follows the colour scheme. */
  tone = 'auto',
  style,
}: {
  width: number;
  tone?: 'auto' | 'ink' | 'white';
  style?: StyleProp<ImageStyle>;
}) {
  const isDark = useIsDark();
  const white = tone === 'white' || (tone === 'auto' && isDark);

  return (
    <Image
      testID="app-logo"
      source={white ? WHITE_INK : DARK_INK}
      accessibilityRole="image"
      // The wordmark IS the product name, so it is labelled rather than hidden: a
      // screen reader must announce "SAMVISION" where a sighted user reads it.
      accessibilityLabel="SAMVISION"
      resizeMode="contain"
      style={[{width, height: Math.round((width / ASPECT) * 100) / 100}, style]}
    />
  );
}

/**
 * The brand MARK — the tile the wordmark sits beside on the frame.
 *
 * Тот же знак, что `BrandMark` ui-kit и favicon: «S» в тёмно-красном квадрате
 * (`assets/branding/mark.png`, растр `ui-kit/assets/branding/favicon.svg`).
 * Квадрат и скругление — в самой картинке. Цвет один на обе темы: цвет марки
 * не зависит от фона.
 */
const MARK = require('../../../assets/branding/mark.png');

export function BrandMark({size = 36}: {size?: number}) {
  return (
    <Image
      source={MARK}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      resizeMode="contain"
      style={{width: size, height: size}}
    />
  );
}
